import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Progress } from 'antd';

import { selectors, fetchDevPlans } from 'state';
import '../developmentPlan.css';

type Props = {
  onSelect: (code: string) => void;
};

/**
 * The always-on landing for the Development Plan tab: a card per published tree.
 * Selecting one enters it (per-tree progress is separate). Trees are provisioned via
 * seed / the admin clone API until the admin authoring UI ships.
 */
const DevPlanPicker: React.FC<Props> = ({ onSelect }) => {
  const dispatch = useDispatch();
  const plans = useSelector(selectors.devPlans);
  const fetched = useSelector(selectors.devPlansFetched);

  useEffect(() => {
    dispatch(fetchDevPlans({}));
  }, [dispatch]);

  if (!fetched) {
    return (
      <div className="devplan devplan--message">Loading your development plans…</div>
    );
  }
  if (!plans.length) {
    return (
      <div className="devplan devplan--message">
        No development plans are available yet.
      </div>
    );
  }

  return (
    <div className="devplan devplan--picker">
      <div className="devplan-picker__head">
        <div className="devplan-picker__title">Development Plans</div>
        <div className="devplan-picker__sub">
          Choose a plan to work on — your progress is saved separately for each.
        </div>
      </div>
      <div className="devplan-picker__grid">
        {plans.map((p) => {
          const pct = p.total_nodes
            ? Math.round((p.unlocked_nodes / p.total_nodes) * 100)
            : 0;
          return (
            <button
              type="button"
              key={p.code}
              className="devplan-picker__card"
              onClick={() => onSelect(p.code)}
            >
              <div className="devplan-picker__card-top">
                <span className="devplan-picker__card-name">{p.name}</span>
                {p.version > 1 && (
                  <span className="devplan-picker__card-badge">v{p.version}</span>
                )}
              </div>
              {p.description && (
                <div className="devplan-picker__card-desc">{p.description}</div>
              )}
              <div className="devplan-picker__card-stats">
                <span>Level {p.level}</span>
                <span className="devplan__muted">
                  {p.unlocked_nodes} / {p.total_nodes} skills
                </span>
              </div>
              <Progress
                percent={pct}
                showInfo={false}
                size="small"
                strokeColor="#0f5fd6"
                trailColor="rgba(15,95,214,0.12)"
              />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DevPlanPicker;
